import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Search, BookOpen, Sparkles, Calendar, ShoppingCart, Users,
  UserCog, DollarSign, Megaphone, Package, GraduationCap, BarChart3,
  Settings, Shield, Zap, HelpCircle, ChevronRight, Lightbulb,
  ArrowRight, CheckCircle2, AlertCircle, Clock, Scissors
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

// ────────── Content ──────────
// Each topic has: id, title, icon, intro, walkthroughs (numbered steps),
// tips, faqs, and a cta to jump to the live module. Edit this array to
// adjust content — no backend required.
const TOPICS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Sparkles,
    intro: "Welcome! Here's the quickest path from empty workspace to first booking in under 30 minutes.",
    walkthroughs: [
      {
        title: "Your first 30 minutes",
        steps: [
          "Open Settings → Branding. Upload your logo and set your app name + brand colors. This flows to the sidebar, emails, and client portal.",
          "Settings → Locations. Add every location you operate from. Each appointment, transaction, and staff schedule lives at a location.",
          "Settings → Services. Review the starter service menu. Edit prices, durations, and categories to match your pricing sheet.",
          "Team → Staff → Add Team Member. Create an owner-level account for yourself and then one account per stylist. Set an initial password or email them a reset link.",
          "On each staff profile, click 'Bulk Add' under Qualified Services to tick every service that team member performs.",
          "Go to Calendar. Click any empty time slot to create a test appointment — this confirms your setup works end-to-end.",
        ],
      },
      {
        title: "Share your public booking link",
        steps: [
          "Settings → Portal Links. Copy the Online Booking URL.",
          "Add it to your Instagram bio, Google Business Profile, and email signature.",
          "New clients will be asked to save a card on file before they can book — this dramatically cuts no-shows.",
        ],
      },
    ],
    tips: [
      "Every module respects your permission matrix. If a stylist shouldn't see Payroll, edit Settings → Access Levels and uncheck 'Payroll — View' on the Base Employee level.",
      "The Command Center (Insights → Command Center) is a shortcut to add/manage clients, services, products, etc. without hunting through menus.",
      "Press Cmd/Ctrl+K from anywhere to open search.",
    ],
    faqs: [
      {
        q: "Can I change the app name shown in the browser tab?",
        a: "Yes — Settings → Branding → App Name. The browser tab updates instantly.",
      },
      {
        q: "How do I invite another owner or manager?",
        a: "Team → Staff → Add Team Member. Set the Role to 'owner' or 'manager'. Send them the login URL and their starter password — they can change it from their profile after logging in.",
      },
    ],
    cta: { label: "Open Settings", to: "/settings" },
  },
  {
    id: "calendar",
    title: "Calendar & Scheduling",
    icon: Calendar,
    intro: "The calendar is your operational heart. Book, reschedule, reassign, and check out — all from one place.",
    walkthroughs: [
      {
        title: "Three views, one calendar",
        steps: [
          "Day view — ideal for a single-location team reviewing today's bookings.",
          "Week view — classic 7-day grid, great for planning ahead.",
          "Staff view — each stylist gets their own column. Perfect for busy multi-chair days.",
          "Toggle between them using the view switcher at the top-right of the calendar.",
        ],
      },
      {
        title: "Reschedule an appointment",
        steps: [
          "Click and drag any appointment block to a new time slot — the system snaps to your zoom increment (5/10/15/30 min).",
          "In Staff view you can drag across columns to reassign to a different stylist. Green = available, red = conflict.",
          "Or click the appointment and edit the time/provider in the dialog. All changes are logged in the Activity tab.",
        ],
      },
      {
        title: "Check out from the calendar",
        steps: [
          "Click any appointment → click the green 'Checkout' button in the dialog.",
          "You'll land in POS with the client, services, and provider pre-filled.",
          "After payment, the appointment status auto-flips to Completed and the Rebook prompt appears.",
        ],
      },
    ],
    tips: [
      "Use the +/− zoom buttons to jump from 30-min slots down to 5-min precision. Useful for busy color-correction days.",
      "Auto-late: any appointment past its start time without check-in turns orange. Tweak the no-show timeout in Settings → Appointments.",
      "Staff view's color bars are keyed to service category — at a glance you can see 'today's all-color day' vs 'balanced menu day'.",
      "Approved time-off appears as a hatched overlay on the day — staff are blocked from being auto-booked.",
    ],
    faqs: [
      {
        q: "Why can't I drag onto a stylist's column in Staff view?",
        a: "Either they're off-shift, on approved PTO, or they're not qualified for that service. Check their profile → Qualified Services.",
      },
      {
        q: "A client shows as 'No-Show' — can I undo that?",
        a: "Yes. Click the appointment and change the status back to 'Booked' or 'Completed'. Every status change is logged in the Activity tab.",
      },
    ],
    cta: { label: "Open Calendar", to: "/calendar" },
  },
  {
    id: "pos",
    title: "POS & Checkout",
    icon: ShoppingCart,
    intro: "Process payments for services, products, gift cards, and memberships. Cash, card on file, or real Stripe.",
    walkthroughs: [
      {
        title: "A typical checkout",
        steps: [
          "Pick the client (or leave blank for a walk-in).",
          "Add services and products to the cart. For each service line, assign the stylist from the dropdown — this powers payroll attribution.",
          "Add tip (fixed amount or %), any discount, and review the tax line.",
          "Choose a payment method: Cash, Card (Stripe checkout), Card-on-File (instant), or a gift card.",
          "Click Complete. You'll see a 'Rebook?' prompt — accept to lock in their next visit before they walk out.",
        ],
      },
      {
        title: "Split tips across stylists",
        steps: [
          "In the tip row, toggle from 'Auto' to 'Manual' split.",
          "Assign tip amounts per stylist. The default 'Auto' splits proportionally to each stylist's service revenue.",
          "Tips attributed this way show in Payroll under each stylist's breakdown.",
        ],
      },
      {
        title: "Save a new card on file",
        steps: [
          "During checkout, click 'Save card' before confirming payment.",
          "For existing clients, card is tokenized via Stripe and stored on their profile.",
          "For new online bookings, card-on-file is required to book — enforced by the public booking flow.",
        ],
      },
    ],
    tips: [
      "The rebook prompt after checkout boosts rebook rates ~3×. Don't skip it.",
      "Products added without service still generate a transaction, but the rebook prompt won't trigger (that only applies when there's a service in the cart).",
      "Type in the search box to filter services by name, category, or SKU instantly.",
    ],
    faqs: [
      {
        q: "Do I need a Stripe account for Card checkouts?",
        a: "We ship with Stripe test mode preconfigured. To go live, connect your own Stripe account in Integrations.",
      },
      {
        q: "Can I sell a gift card and use it in the same transaction?",
        a: "Yes — issue the gift card first (Sales → Gift Cards), then redeem the code in checkout. The client gets the discount immediately.",
      },
    ],
    cta: { label: "Open Checkout", to: "/pos" },
  },
  {
    id: "clients",
    title: "Clients & CRM",
    icon: Users,
    intro: "Every client gets a rich profile — history, color formulas, loyalty, scoring, and notes in one place.",
    walkthroughs: [
      {
        title: "What's on a client profile",
        steps: [
          "Details tab — contact info, pipeline stage, tags, LTV, visit count.",
          "Appointments tab — full history with revenue attribution.",
          "Color Formulas tab — save, version, and retrieve the exact formula used on each client. Every edit keeps the previous version in history.",
          "Transactions tab — all POS checkouts, tips, payment methods.",
          "Client Score card — 0–100 score with 7-category breakdown and recommended actions.",
          "Activity Log — every admin action (ban, deactivate, merge) is auditable.",
        ],
      },
      {
        title: "Understand the Client Score",
        steps: [
          "Scores are weighted by: Total Spend (25%), Rebooking (20%), Product Purchases (15%), Reviews (10%), Loyalty (10%), Attendance (10%), Recency (10%).",
          "Tiers: Elite VIP, VIP, Loyal Core, Opportunity, At Risk, Needs Attention.",
          "Clients → Scores to see the leaderboard. At-Risk VIPs and high-value-no-reviews flags prompt outreach.",
          "Tune the weights in /client-scores → Settings if your business model is different (e.g., more weight on product for a retail-heavy shop).",
        ],
      },
    ],
    tips: [
      "Searching is instant — start typing in the clients list and results filter live.",
      "Pipeline stages (Lead → First Visit → Regular → VIP → Lapsed) help segment marketing campaigns.",
      "Use bulk actions to add tags (like 'Winter event 2026') to dozens of clients at once.",
    ],
    faqs: [
      {
        q: "Can I merge two duplicate client profiles?",
        a: "Open the duplicate → click the gear menu → Merge into… then pick the master record. All appointments, transactions, and notes move over.",
      },
      {
        q: "A client is banned. What happens?",
        a: "They can't book online. Any existing future appointment stays on the calendar unless you cancel it. The ban is logged in the client's activity log.",
      },
    ],
    cta: { label: "Open Clients", to: "/clients" },
  },
  {
    id: "staff",
    title: "Team & Staff",
    icon: UserCog,
    intro: "Manage staff, schedules, pay, permissions, training, and per-staff custom pricing.",
    walkthroughs: [
      {
        title: "Onboard a new stylist",
        steps: [
          "Team → Staff → Add Team Member. Enter name, email, phone, role, location(s).",
          "Set an initial password so they can sign in immediately, or email a reset link.",
          "On their profile, set Access Level (Owner / Manager / Base Employee / Front Desk) — each controls 34 modules.",
          "Click 'Bulk Add' under Qualified Services to tick every service they perform.",
          "Schedule tab — add their recurring weekly shifts with the repeat-weekly checkbox.",
          "Optional: set a 4-digit PIN so they can clock in at the wall-mounted tablet kiosk.",
        ],
      },
      {
        title: "Per-staff custom pricing",
        steps: [
          "Every qualification can override the menu price for that stylist.",
          "Senior stylists often charge $10–20 more; junior stylists $10–15 less than the menu.",
          "When booking, the calendar + rebook + public site all use the per-stylist price automatically.",
          "Empty custom price = use the menu base price.",
        ],
      },
      {
        title: "Revoke sessions and reset passwords",
        steps: [
          "On a staff profile → Reset Password → pick 'Email link' or 'Set manually'.",
          "When you reset, their existing sessions are instantly revoked (all devices, globally).",
          "This is the safest way to lock out a terminated employee — no waiting for token expiration.",
        ],
      },
    ],
    tips: [
      "Educator role: flip the 'Is educator?' switch on senior staff. Educators appear in Training Studio signoff queues.",
      "Owner-on-owner deletion requires a manager PIN + at least one other owner remaining (can't delete the last owner).",
      "Deactivated staff are hidden from the booking pool everywhere: public site, calendar suggestions, rebook dialog.",
    ],
    faqs: [
      {
        q: "What's the difference between Deactivate and Delete?",
        a: "Deactivate keeps their history (appointments, payroll) but hides them from the booking pool. Delete permanently removes all their data including shifts, timeclock entries, and qualifications. Future appointments get unassigned.",
      },
      {
        q: "How do I mark someone as an Educator?",
        a: "Open their profile → toggle 'Is educator?' → Save. They'll show up in the Training module's signoff dropdowns.",
      },
    ],
    cta: { label: "Open Team", to: "/staff" },
  },
  {
    id: "payroll",
    title: "Payroll & Commissions",
    icon: DollarSign,
    intro: "Automated commission tiers based on 7-day rolling service sales, plus tips, hourly, and bonuses.",
    walkthroughs: [
      {
        title: "Set up your commission tiers",
        steps: [
          "Payroll → Commission Settings.",
          "Add tiers — e.g., 40% at $1,250/wk, 42% at $1,500, 43% at $1,750.",
          "Each stylist's 7-day rolling service revenue places them at the current tier.",
          "Save — the tiers apply instantly across payroll reports.",
        ],
      },
      {
        title: "Configure the pay period",
        steps: [
          "Settings → Pay Period. Pick weekly, bi-weekly, semi-monthly, or monthly.",
          "Set an anchor date — any pay-date in the past; the system rolls forward from there.",
          "Payroll calculations now use this window.",
        ],
      },
      {
        title: "Read the commission report",
        steps: [
          "Payroll → Summary. Each card: week sales, tier, rate, revenue, commission, tips, total pay.",
          "Bar chart shows daily breakdown per stylist.",
          "Numbers include revenue from appointments + POS (de-duplicated using max of the two).",
        ],
      },
    ],
    tips: [
      "Missing commission? Check the stylist is assigned on the service line items in POS (not just the appointment).",
      "Tier progress bars show how close each stylist is to their next tier — great coaching tool.",
      "Hourly staff can be paid via the 'pay_type' flag on their profile. Pure-hourly staff ignore the commission tiers.",
    ],
    faqs: [
      {
        q: "Can I add deductions or reimbursements?",
        a: "Not yet — PDF export and manual adjustments are on the roadmap (P1).",
      },
    ],
    cta: { label: "Open Payroll", to: "/payroll" },
  },
  {
    id: "marketing",
    title: "Marketing, Automations & Rebook",
    icon: Megaphone,
    intro: "Win-back lapsed clients, celebrate birthdays, and automate the stuff you'd otherwise forget.",
    walkthroughs: [
      {
        title: "Send a campaign",
        steps: [
          "Marketing → Campaigns → New.",
          "Segment: pipeline stage, tags, last visit window, score tier.",
          "Channel: Email or SMS. The AI Copy button drafts a first pass using GPT that you can edit.",
          "Preview, send, or schedule.",
        ],
      },
      {
        title: "Due-for-Rebook campaigns",
        steps: [
          "Marketing → Due for Rebook.",
          "The system compares days-since-last-visit against each client's average gap.",
          "Three urgency levels: Critical, Overdue, Due Soon. Filter by tab.",
          "Bulk-select and Send Reminders to fire templated SMS with {name} and {services} placeholders.",
        ],
      },
      {
        title: "Build a multi-step automation",
        steps: [
          "Marketing → Automations → New Workflow.",
          "Start with a trigger (e.g., appointment completed).",
          "Add delay/condition/action steps. Condition steps branch — e.g., 'if no review received in 7 days, send follow-up'.",
          "Test with the step-by-step runner before activating.",
        ],
      },
    ],
    tips: [
      "Real SendGrid/Twilio sending requires API keys (Integrations → SendGrid / Twilio). Until then, campaigns log as simulated sends.",
      "Score-tier-based segments are powerful: target 'At Risk VIPs' with a handwritten-style win-back instead of generic blasts.",
    ],
    faqs: [
      {
        q: "Can I pause an automation without deleting it?",
        a: "Yes — the active toggle on each workflow stops new runs. In-flight runs continue to completion.",
      },
    ],
    cta: { label: "Open Marketing", to: "/marketing" },
  },
  {
    id: "inventory",
    title: "Inventory & Retail",
    icon: Package,
    intro: "Track stock, reorder automatically, and see real margin by product and category.",
    walkthroughs: [
      {
        title: "Set up auto-reorder",
        steps: [
          "Inventory → Products. Open each product and set its PAR level and reorder quantity.",
          "Inventory → Vendors. Set minimum order threshold for each vendor.",
          "Inventory → Auto-Reorder. Click 'Generate POs'. The system creates purchase orders grouped by vendor, skipping vendors whose total falls below their minimum.",
          "Review, send to vendor (simulated email), then mark received — stock levels auto-update.",
        ],
      },
      {
        title: "Bulk import products",
        steps: [
          "Inventory → Products → Bulk Import. Download the CSV template.",
          "Fill in name, SKU, category, cost, retail price, vendor, PAR level, starting stock.",
          "Upload. Validation errors show per-row — fix and re-upload.",
        ],
      },
    ],
    tips: [
      "Inventory value dashboard shows cost value vs retail value — that gap is your gross margin locked in inventory.",
      "Low-stock alerts fire when on-hand falls below PAR. Great morning-dashboard signal.",
      "Attach products to services in Settings → Services to track which products actually get used during appointments.",
    ],
    faqs: [
      {
        q: "Can I transfer stock between locations?",
        a: "Yes — each stock adjustment has a reason code. Pick 'Transfer' and note the destination in the reason field.",
      },
    ],
    cta: { label: "Open Inventory", to: "/inventory" },
  },
  {
    id: "training",
    title: "Training Studio",
    icon: GraduationCap,
    intro: "Service training tracks, skill signoffs, evaluations, and a shared content library.",
    walkthroughs: [
      {
        title: "Build a service training track",
        steps: [
          "Training → Tracks → New Track.",
          "Pick the service (e.g., Balayage). Set skill level (Junior / Intermediate / Advanced).",
          "Add competencies with weights — e.g., 'Sectioning (30%)', 'Tone selection (40%)', 'Blend (30%)'.",
          "Add quality/consultation/finishing standards — the evaluator sees these during scoring.",
          "Set approval requirements (which signoffs needed: educator, manager).",
        ],
      },
      {
        title: "Score an evaluation",
        steps: [
          "Training → Evaluations → New Evaluation. Pick the track — the competency scorecard auto-populates.",
          "Star-rate each competency (1–5). Auto-weighted total.",
          "Add strengths, improvements, formula notes. Attach before/after photos.",
          "Result: pass, needs_work, or fail. Pass signoffs push the trainee along the approval pipeline.",
        ],
      },
    ],
    tips: [
      "Approval stages: Not Ready → Model Approved → Supervised → Independent → Certified.",
      "The Employee Profiles tab shows each stylist's approval status per service — great for assignment decisions.",
      "Resource library is searchable and track-linked so educators can point to 'the balayage SOP' in seconds.",
    ],
    cta: { label: "Open Training", to: "/training" },
  },
  {
    id: "reports",
    title: "Reports & Insights",
    icon: BarChart3,
    intro: "Revenue, performance, risk, and multi-location roll-ups — everything you need for your weekly ops review.",
    walkthroughs: [
      {
        title: "Your weekly review routine",
        steps: [
          "Executive dashboard — top-level revenue and MoM comparison per location.",
          "Reports — revenue trends, category breakdown, provider leaderboard.",
          "Location Pulse Board — operational pressure (utilization, staffing gaps, escalations).",
          "Team Performance — 10-metric scoring with coaching queue for anyone under 60.",
          "No-Show Risk — clients flagged for deposit enforcement.",
        ],
      },
    ],
    tips: [
      "Location Pulse auto-refreshes every 60s with push toasts when a location drops to At Risk.",
      "Team Performance's radar chart highlights individual strengths vs weaknesses — use it for 1:1s.",
      "Filter any report by date range, location, or provider to drill in.",
    ],
    cta: { label: "Open Reports", to: "/reports" },
  },
  {
    id: "settings",
    title: "Settings, Access & Integrations",
    icon: Settings,
    intro: "Configure branding, access levels, pay period, integrations, and public portal links.",
    walkthroughs: [
      {
        title: "Lock down access",
        steps: [
          "Settings → Access Levels. Four system levels ship out of the box: Owner (all), Manager, Base Employee (limited), Front Desk.",
          "Click any level → toggle module permissions (View / Create / Edit / Delete) across 34 modules.",
          "Create custom levels for edge cases (e.g., 'Booth Renter' with only Calendar + Clients).",
          "Assign the level on each staff profile.",
        ],
      },
      {
        title: "Connect integrations",
        steps: [
          "Integrations hub — 10 app cards (Stripe, SendGrid, Twilio, Mailchimp, Google Reviews, QuickBooks, etc.).",
          "Click Set Up, paste API keys, Save & Connect. Test Connection verifies.",
          "Webhooks — register URL endpoints to receive real-time event notifications (12 event types: appointment.created, transaction.completed, etc.).",
        ],
      },
      {
        title: "Pair a wall-mounted clock-in kiosk",
        steps: [
          "Settings → Locations → pick a location. Reveal the kiosk pairing code.",
          "On the tablet, open /kiosk and enter the 6-digit code.",
          "Staff tap their name → enter their 4-digit PIN to clock in/out. Managers can review missed-punch requests in the TimeClock module.",
        ],
      },
    ],
    tips: [
      "Manager PIN (Settings → Manager PIN) is required for every destructive action in Command Center — protects against accidental deletions.",
      "Portal Links (Settings → Portal Links) has copy-to-clipboard URLs for every client-facing page — great for marketing materials.",
    ],
    cta: { label: "Open Settings", to: "/settings" },
  },
  {
    id: "faq",
    title: "Troubleshooting & FAQ",
    icon: HelpCircle,
    intro: "Common questions and fixes for issues you might hit in the first 90 days.",
    walkthroughs: [],
    tips: [
      "'No openings' in the Rebook dialog? The stylist likely isn't qualified for that service. Go to their profile → Bulk Add → tick the service.",
      "A stylist can't log in? Reset their password via Team → Staff → their profile → Reset Password. Old sessions are revoked instantly.",
      "Stripe checkout failing? Confirm Integrations → Stripe has valid live keys. Test mode is enabled by default for sandboxing.",
      "Slow on a tablet? Collapse the sidebar (arrow at bottom-left) and use Day view instead of Week view.",
    ],
    faqs: [
      {
        q: "How do I back up my data?",
        a: "Your workspace is backed up continuously on the platform. Export per-module is coming soon (P1).",
      },
      {
        q: "Can I operate multiple brands from one account?",
        a: "Yes — each brand lives in its own tenant with full data isolation. Contact support to set up additional tenants under one billing account.",
      },
      {
        q: "Where do I change the URL slug for my workspace?",
        a: "The slug is locked at signup for data integrity. Contact support if you need it changed — we can migrate safely.",
      },
    ],
  },
];

// ────────── Component ──────────
export default function HelpCenterPage() {
  const [activeId, setActiveId] = useState("getting-started");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return TOPICS;
    return TOPICS.filter(t => {
      if (t.title.toLowerCase().includes(q)) return true;
      if (t.intro.toLowerCase().includes(q)) return true;
      const matchesWalk = (t.walkthroughs || []).some(w =>
        w.title.toLowerCase().includes(q) ||
        w.steps.some(s => s.toLowerCase().includes(q))
      );
      if (matchesWalk) return true;
      if ((t.tips || []).some(tip => tip.toLowerCase().includes(q))) return true;
      if ((t.faqs || []).some(f => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [search]);

  const active = useMemo(
    () => filtered.find(t => t.id === activeId) || filtered[0] || TOPICS[0],
    [filtered, activeId]
  );

  const ActiveIcon = active?.icon || BookOpen;

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="help-center-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-400 flex items-center justify-center shadow-lg shadow-rose-500/20">
            <BookOpen className="w-5 h-5 text-white" strokeWidth={2.2} />
          </div>
          <div>
            <h1 className="font-['Outfit'] text-3xl font-semibold text-slate-900 tracking-tight">Help Center</h1>
            <p className="text-sm text-slate-500 font-['Manrope']">Walkthroughs, tips, and tricks for running your salon on SalonFlow</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mt-6 max-w-lg" data-testid="help-search-wrapper">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search the help center (e.g., 'rebook', 'payroll', 'kiosk')"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 h-11 rounded-full border-slate-200 font-['Manrope'] bg-white"
            data-testid="help-search-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        {/* Sidebar nav */}
        <nav className="bg-white border border-slate-200 rounded-2xl p-2 h-fit sticky top-6" data-testid="help-sidebar">
          {filtered.length === 0 ? (
            <div className="p-4 text-xs text-slate-400 font-['Manrope'] text-center">No topics match "{search}"</div>
          ) : (
            <div className="space-y-0.5">
              {filtered.map(t => {
                const Icon = t.icon;
                const isActive = active?.id === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveId(t.id)}
                    data-testid={`help-topic-${t.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition font-['Manrope']
                      ${isActive
                        ? "bg-rose-50 text-rose-700 font-semibold"
                        : "text-slate-600 hover:bg-slate-50"}`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-rose-600" : "text-slate-400"}`} strokeWidth={2} />
                    <span className="text-sm truncate">{t.title}</span>
                    {isActive && <ChevronRight className="w-3.5 h-3.5 ml-auto text-rose-500" />}
                  </button>
                );
              })}
            </div>
          )}
        </nav>

        {/* Article */}
        {active && (
          <article className="space-y-8" data-testid={`help-article-${active.id}`}>
            {/* Title card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center flex-shrink-0">
                  <ActiveIcon className="w-6 h-6 text-rose-600" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight mb-2">{active.title}</h2>
                  <p className="text-sm text-slate-600 font-['Manrope'] leading-relaxed">{active.intro}</p>
                  {active.cta && (
                    <Link to={active.cta.to} className="inline-flex mt-4" data-testid={`help-cta-${active.id}`}>
                      <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full font-['Manrope'] text-xs h-9 px-4">
                        {active.cta.label} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* Walkthroughs */}
            {active.walkthroughs && active.walkthroughs.length > 0 && (
              <section>
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-3 font-['Manrope'] flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5" />Walkthroughs
                </h3>
                <div className="space-y-4">
                  {active.walkthroughs.map((w, wi) => (
                    <div key={`${active.id}-walk-${wi}`} className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={`help-walkthrough-${active.id}-${wi}`}>
                      <h4 className="font-['Outfit'] text-lg font-medium text-slate-900 mb-4">{w.title}</h4>
                      <ol className="space-y-3">
                        {w.steps.map((s, si) => (
                          <li key={`${active.id}-walk-${wi}-step-${si}`} className="flex gap-3 text-sm text-slate-700 font-['Manrope'] leading-relaxed">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center mt-0.5">
                              {si + 1}
                            </span>
                            <span className="pt-0.5">{s}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Tips */}
            {active.tips && active.tips.length > 0 && (
              <section>
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-3 font-['Manrope'] flex items-center gap-2">
                  <Lightbulb className="w-3.5 h-3.5" />Tips & tricks
                </h3>
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-2xl p-6">
                  <ul className="space-y-3">
                    {active.tips.map((t, ti) => (
                      <li key={`${active.id}-tip-${ti}`} className="flex gap-3 text-sm text-slate-700 font-['Manrope'] leading-relaxed" data-testid={`help-tip-${active.id}-${ti}`}>
                        <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            )}

            {/* FAQs */}
            {active.faqs && active.faqs.length > 0 && (
              <section>
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-3 font-['Manrope'] flex items-center gap-2">
                  <AlertCircle className="w-3.5 h-3.5" />Frequently asked
                </h3>
                <div className="space-y-2">
                  {active.faqs.map((f, fi) => (
                    <details key={`${active.id}-faq-${fi}`} className="group bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`help-faq-${active.id}-${fi}`}>
                      <summary className="cursor-pointer list-none p-4 flex items-center justify-between hover:bg-slate-50 transition">
                        <span className="text-sm font-medium text-slate-900 font-['Manrope'] pr-4">{f.q}</span>
                        <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition flex-shrink-0" />
                      </summary>
                      <div className="px-4 pb-4 text-sm text-slate-600 font-['Manrope'] leading-relaxed border-t border-slate-100 pt-3">
                        {f.a}
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {/* Footer nav */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-400 font-['Manrope']">
                Can't find what you need? Email support or message your account manager.
              </p>
              {active.cta && (
                <Link to={active.cta.to}>
                  <Button variant="outline" className="rounded-full text-xs h-9 font-['Manrope']">
                    {active.cta.label} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </Link>
              )}
            </div>
          </article>
        )}
      </div>
    </div>
  );
}
